import type { ReportColumn } from '../models/column.types'
import type { ReportRow } from '../models/report.types'
import type {
  BuildTrendReportSpecPatchInput,
  BuildTrendTablePresentationInput,
  TrendReportSpecPatch,
  TrendTableFrequency,
  TrendTablePresentation,
  TrendTableRow,
} from '../models/trendTable.types'

import { reportTableRowIdentity } from './tableRowIdentity'

const LABEL_FIELD = '__trendLabel'
const BUCKET_PREFIX = '__trendBucket'

const BUCKET_FIELDS: Record<TrendTableFrequency, 'date' | 'week' | 'month'> = {
  daily: 'date',
  weekly: 'week',
  monthly: 'month',
}

function isReportRow(value: unknown): value is ReportRow {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function breakdownValues(row: ReportRow): ReportRow[] {
  const edges = Array.isArray(row.edges) ? row.edges.filter(isReportRow) : []
  if (edges.length > 0) return edges
  const value = row['breakdown_values']
  return Array.isArray(value) ? value.filter(isReportRow) : []
}

function normalizedBucket(value: unknown): string {
  return String(value ?? '').replace(/T00:00:00(?:\.000)?Z?$/, '')
}

function pointForBucket(
  points: readonly ReportRow[],
  bucket: ReportColumn,
  frequency: TrendTableFrequency,
): ReportRow | undefined {
  const activeField = BUCKET_FIELDS[frequency]
  const target = normalizedBucket(bucket.field)
  return points.find((point) => {
    const activeValue = point[activeField]
    const legacyDateValue = point['date']
    return normalizedBucket(activeValue) === target || normalizedBucket(legacyDateValue) === target
  })
}

function visibleMetrics(columns: readonly ReportColumn[]): ReportColumn[] {
  return columns.filter(
    (column) => column.numeric && !column.invisible && column.selected !== false,
  )
}

function trendKey(identity: string, occurrence: number): string {
  return `trend:${JSON.stringify([identity, occurrence])}`
}

function buildMetricRow(
  parentKey: string,
  metric: ReportColumn,
  points: readonly ReportRow[],
  buckets: readonly ReportColumn[],
  frequency: TrendTableFrequency,
): TrendTableRow {
  return {
    __trendKey: `trend-metric:${JSON.stringify([parentKey, metric.field])}`,
    __trendKind: 'metric',
    __trendLabel: metric.header,
    __trendMetricField: metric.field,
    __trendChildren: [],
    ...Object.fromEntries(
      buckets.map((bucket, index) => [
        `${BUCKET_PREFIX}${index}`,
        pointForBucket(points, bucket, frequency)?.[metric.field],
      ]),
    ),
  }
}

/** Build the legacy breakdown x time-bucket grid as hierarchical, immutable DataTable rows. */
export function buildTrendTablePresentation(
  input: BuildTrendTablePresentationInput,
): TrendTablePresentation {
  const metrics = visibleMetrics(input.columns)
  const bucketFields = input.dateDimensions.map((_, index) => `${BUCKET_PREFIX}${index}`)
  const columns: ReportColumn[] = [
    { ...input.breakdownColumn, field: LABEL_FIELD, frozen: true, sortable: false },
    ...input.dateDimensions.map((bucket, index) => ({
      ...bucket,
      field: bucketFields[index] as string,
      numeric: true,
      sortable: false,
      meta: { ...(bucket.meta ?? {}), trendBucket: bucket.field },
    })),
  ]
  const occurrences = new Map<string, number>()
  const identityColumns = input.columns.some(
    (column) => column.field === input.breakdownColumn.field,
  )
    ? input.columns
    : [input.breakdownColumn, ...input.columns]
  const rows = input.data.map((source, index): TrendTableRow => {
    const label = source[input.breakdownColumn.field]
    const identity =
      reportTableRowIdentity(source, identityColumns) ?? `source-index:${String(index)}`
    const occurrence = occurrences.get(identity) ?? 0
    occurrences.set(identity, occurrence + 1)
    const parentKey = trendKey(identity, occurrence)
    const points = breakdownValues(source)
    return {
      __trendKey: parentKey,
      __trendKind: 'summary',
      __trendLabel: label,
      __trendMetricField: input.previewColumn.field,
      __trendChildren: metrics.map((metric) =>
        buildMetricRow(parentKey, metric, points, input.dateDimensions, input.frequency),
      ),
      ...Object.fromEntries(
        input.dateDimensions.map((bucket, index) => [
          bucketFields[index] as string,
          pointForBucket(points, bucket, input.frequency)?.[input.previewColumn.field],
        ]),
      ),
    }
  })
  return { columns, rows, bucketFields }
}

export function trendBucketField(frequency: TrendTableFrequency): 'date' | 'week' | 'month' {
  return BUCKET_FIELDS[frequency]
}

/** Request patch for the controller's legacy trend response shape. */
export function buildTrendReportSpecPatch(
  input: BuildTrendReportSpecPatchInput,
): TrendReportSpecPatch {
  return {
    dimensions: [input.breakdownField],
    breakdown: trendBucketField(input.frequency),
    sort_field: input.previewMetricField,
    sort_order: -1,
  }
}
